import { components, useApi } from "@appretio/api";
import { ContextSelectedOptions } from "@appretio/context-selector";
import { useQuery } from "@tanstack/react-query";
import { TemporalContext } from "../../../components/temporalContextSelector/data/temporalContextOptions";
import { convertOptionsToApiFormat } from "../utils/convertToApiFormat";
import { topTenQueryKeys } from "./queryKeys";

/**
 * Response type for top-ten data
 */

export type TopTenResponse =
  components["schemas"]["InSeasonReviewDataProduct"] & {
    computedRank: number;
  };

/**
 * Sort column options for top-ten data
 */
export type TopTenSortColumn = "time_purchase" | "nis3";

/**
 * Generic hook to fetch top-ten data
 * @param contextOptions - Selected context options
 * @param temporalOptions - Selected temporal options
 * @param enabled - Whether the query should be enabled
 * @param column - Column to sort by
 * @param direction - Sort direction
 * @param isPurchase - Whether to fetch purchase data (true) or sales data (false)
 * @returns Query result containing top-ten data
 */

function useFetchTopTen(
  contextOptions: ContextSelectedOptions,
  temporalOptions: TemporalContext,
  enabled: boolean = false,
  isPurchase: boolean = false,
  limit: number = 10
) {
  const apiClient = useApi();

  return useQuery({
    queryKey: isPurchase
      ? topTenQueryKeys.purchaseData(contextOptions, temporalOptions)
      : topTenQueryKeys.salesData(contextOptions, temporalOptions),
    queryFn: async () => {
      const options = convertOptionsToApiFormat(
        contextOptions,
        temporalOptions
      );

      try {
        const response = await apiClient.POST(
          "/v1/in-season/review/get-review-data",
          {
            body: {
              ...options,
              query_params: {
                offset: 0,
                limit: limit,
                sorts: [
                  {
                    column: isPurchase ? "time_purchase" : "nis3",
                    direction: "desc"
                  }
                ]
              }
            }
          }
        );

        const data = (response.data?.result as TopTenResponse[]) ?? [];
        const topTenData = data.map((row, index) => ({
          ...row,
          computedRank: index + 1
        }));

        return topTenData;
      } catch (error) {
        console.error("Error fetching top-ten data:", error);
        throw error;
      }
    },
    enabled,
    staleTime: 5 * 60 * 1000
  });
}

/**
 * Fetches top-ten sales data sorted by NS3 (Net Sales)
 */
export function useFetchTopTenSales(
  contextOptions: ContextSelectedOptions,
  temporalOptions: TemporalContext,
  enabled: boolean = false
) {
  return useFetchTopTen(contextOptions, temporalOptions, enabled, false);
}

/**
 * Fetches top-ten purchase data sorted by time_purchase
 */
export function useFetchTopTenPurchase(
  contextOptions: ContextSelectedOptions,
  temporalOptions: TemporalContext,
  enabled: boolean = false
) {
  return useFetchTopTen(contextOptions, temporalOptions, enabled, true);
}
